// Ported from dev-scrips-pm-react src/billing/services/use-package-grants.ts (PROD-3312 appointment Billing tab).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import type {
  PackageGrant,
  PackageGrantRedemptionHistory,
  PackageRedemption,
  RefundPackageGrantRequest,
  RefundPackageGrantResponse,
  SellPackageRequest,
  SellPackageResponse,
} from '@/shared/types/billing';

/**
 * A patient's purchased entitlements (PROD-2126).
 *
 *   POST /PackageGrants                                   — sell: wallet receipt + grant
 *   GET  /PackageGrants/patient/{id}?includeExhausted     — units purchased/used/remaining
 *   GET  /PackageGrants/invoice/{id}/redemptions          — what a package paid on an invoice
 *   GET  /PackageGrants/{id}/redemptions                  — one grant's consumption history (PROD-2151)
 *   POST /PackageGrants/{id}/refund                       — refund the unused sessions (PROD-2151)
 *
 * Consumption is server-side at invoice issue; the UI only reads it back.
 */

export const PACKAGE_GRANTS_KEY = ['billing', 'package-grants'] as const;

export function patientGrantsKey(patientId: string) {
  return [...PACKAGE_GRANTS_KEY, 'patient', patientId] as const;
}

export const grantHistoryKey = (grantId: string) =>
  [...PACKAGE_GRANTS_KEY, 'grant', grantId, 'redemptions'] as const;

export function usePatientPackageGrants(patientId: string | null | undefined, includeExhausted = true) {
  return useQuery({
    queryKey: [...patientGrantsKey(patientId ?? ''), includeExhausted] as const,
    queryFn: ({ signal }) =>
      get<PackageGrant[]>(
        `${BILLING_BASE}/PackageGrants/patient/${patientId}?includeExhausted=${includeExhausted}`,
        { signal },
      ),
    enabled: Boolean(patientId),
  });
}

export function useInvoicePackageRedemptions(invoiceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...PACKAGE_GRANTS_KEY, 'invoice', invoiceId] as const,
    queryFn: ({ signal }) =>
      get<PackageRedemption[]>(`${BILLING_BASE}/PackageGrants/invoice/${invoiceId}/redemptions`, { signal }),
    enabled: enabled && Boolean(invoiceId),
  });
}

export function useSellPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: SellPackageRequest) =>
      post<SellPackageResponse>(`${BILLING_BASE}/PackageGrants`, request),
    onSuccess: (_data, vars) => {
      showSuccess('Package sold');
      void qc.invalidateQueries({ queryKey: patientGrantsKey(vars.patientId) });
      // The sale is also a wallet receipt (IsAvailableBalance = 1): the receipts
      // list and the balance card must move together with the grants card.
      void qc.invalidateQueries({ queryKey: ['patient-advance-receipts', vars.patientId] });
      invalidateBalanceViews(qc);
    },
  });
}

/** One grant's consumption history - which invoice ate which units (PROD-2151). */
export function usePackageGrantRedemptions(grantId: string | null | undefined, enabled = true) {
  return useQuery({
    // A sentinel, not '': an empty id would share one cache slot with any
    // other id-less caller of grantHistoryKey.
    queryKey: grantHistoryKey(grantId ?? '__none__'),
    enabled: Boolean(grantId) && enabled,
    queryFn: ({ signal }) =>
      get<PackageGrantRedemptionHistory[]>(
        `${BILLING_BASE}/PackageGrants/${grantId}/redemptions`,
        { signal },
      ),
  });
}

export interface RefundPackageGrantVars {
  grantId: string;
  patientId: string;
  request: RefundPackageGrantRequest;
}

export function useRefundPackageGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: RefundPackageGrantVars) =>
      post<RefundPackageGrantResponse>(
        `${BILLING_BASE}/PackageGrants/${vars.grantId}/refund`,
        vars.request,
      ),
    onSuccess: (_res, vars) => {
      showSuccess('Unused sessions refunded');
      void qc.invalidateQueries({ queryKey: patientGrantsKey(vars.patientId) });
      // The grant's own history changes too (the refund closes it).
      void qc.invalidateQueries({ queryKey: grantHistoryKey(vars.grantId) });
      // The refund closes the grant AND writes against the sale receipt, so the
      // receipts list, the refunds list and the balance views all move with it.
      void qc.invalidateQueries({ queryKey: ['patient-advance-receipts', vars.patientId] });
      void qc.invalidateQueries({ queryKey: ['patient-refunds-list', vars.patientId] });
      invalidateBalanceViews(qc);
    },
  });
}
